import React, { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ExternalLink, Megaphone, PartyPopper, X } from "lucide-react";
import PageHeader from "components/page-header";
import { Button } from "components/ui/button";
import Loading from "components/loading";
import DashboardDropdown from "screens/partials/ecommerce/components/dashboard-dropdown";
import { useOpenBoost, naira } from "lib/boost";
import { useUserData } from "store/user-data";
import { Campaign, getAdsSummaryAction, getCampaignsAction, timeframeDays } from "action/ads-action";
import {
  AgeGenderBars,
  CampaignStatus,
  EmptyNote,
  Funnel,
  LocationList,
  PerformanceChart,
  PlacementTable,
  Section,
  StatTiles,
  count,
  shortDate,
  statTiles,
  timeAgo,
} from "./parts";
import PixelCard from "./pixel-card";

export default function StorboltMetaAdsComponent() {
  const { userData } = useUserData();
  const businessId = userData?.business_id;
  const openBoost = useOpenBoost();
  const [timeframe, setTimeframe] = useState("28d");
  const [searchParams, setSearchParams] = useSearchParams();
  // Paystack sends the vendor back here with ?reference=… after paying for a boost.
  const justPaid = !!(searchParams.get("reference") || searchParams.get("trxref"));

  const { data: summaryResponse, isLoading: summaryLoading } = useQuery({
    queryKey: ["ads-summary", businessId, timeframe],
    queryFn: () => getAdsSummaryAction(timeframeDays(timeframe)),
    enabled: !!businessId,
  });
  const { data: campaignsResponse, isLoading: campaignsLoading } = useQuery({
    queryKey: ["ads-campaigns", businessId],
    queryFn: () => getCampaignsAction(),
    enabled: !!businessId,
    // Right after paying, the webhook may land a moment later: check again shortly.
    refetchInterval: justPaid ? 15000 : false,
  });

  const summary = summaryResponse?.error ? null : summaryResponse?.body;
  const campaigns: Campaign[] = Array.isArray(campaignsResponse?.body) ? campaignsResponse!.body : [];
  const hasResults = !!summary && summary.campaign_count > 0;

  return (
    <div className="space-y-5">
      <PageHeader
        eyebrow="Marketing"
        title="Meta Ads"
        description="Boost your store or a product on Facebook and Instagram. We run the ad for you and show the results here."
        actions={
          <>
            <DashboardDropdown value={timeframe} onChange={setTimeframe} />
            <Button onClick={() => openBoost()}>
              <Megaphone className="mr-2 h-4 w-4" />
              Boost on Meta Ads
            </Button>
          </>
        }
      />

      {justPaid && (
        <div className="flex items-start gap-3 rounded-20px bg-sage p-4 text-sage-foreground">
          <PartyPopper className="mt-0.5 h-5 w-5 flex-none" />
          <div className="flex-1 text-sm">
            <div className="font-semibold">Thanks! We're setting up your ad.</div>
            <p className="mt-0.5">
              Once your payment is confirmed our team builds your ad on Meta. You'll see it below as “Setting up”, then
              “Active” when it goes live.
            </p>
          </div>
          <button
            type="button"
            className="rounded-full p-1 hover:bg-black/5"
            aria-label="Dismiss"
            onClick={() => setSearchParams({}, { replace: true })}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {summaryLoading ? (
        <Loading />
      ) : !hasResults ? (
        <Section title="Get more customers with Meta Ads">
          <div className="flex flex-col items-start gap-4 md:flex-row md:items-center md:justify-between">
            <p className="max-w-xl text-sm text-default-600">
              Choose your storefront or a product, set a budget, and our team puts your ad in front of people on Facebook,
              Instagram and Messenger. Views, clicks and sales from the ad show up on this page.
            </p>
            <Button onClick={() => openBoost()}>
              <Megaphone className="mr-2 h-4 w-4" />
              Boost your first ad
            </Button>
          </div>
        </Section>
      ) : (
        <>
          <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-default-500">
            <span>
              {summary!.campaign_count} campaign{summary!.campaign_count === 1 ? "" : "s"} · {summary!.active_count} active
            </span>
            {summary!.last_synced_at && <span>Data last synced {timeAgo(summary!.last_synced_at)}</span>}
          </div>
          <StatTiles tiles={statTiles(summary!.totals, summary!.totals.metrics)} />

          <div className="grid grid-cols-1 gap-5 lg:grid-cols-5">
            <Section title="From view to purchase" className="lg:col-span-2">
              <Funnel totals={summary!.totals} />
            </Section>
            <Section title="Performance over time" className="lg:col-span-3">
              <PerformanceChart daily={summary!.daily} />
            </Section>
          </div>

          <Section title="Results by placement">
            <PlacementTable rows={summary!.by_placement} />
          </Section>
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
            <Section title="Who saw your ads">
              <AgeGenderBars rows={summary!.by_age_gender} />
            </Section>
            <Section title="Top locations">
              <LocationList rows={summary!.by_location} />
            </Section>
          </div>
        </>
      )}

      <Section title="Your campaigns">
        {campaignsLoading ? (
          <Loading />
        ) : campaigns.length === 0 ? (
          <EmptyNote>No campaigns yet. Your boosts will be listed here.</EmptyNote>
        ) : (
          <CampaignList campaigns={campaigns} />
        )}
      </Section>

      <PixelCard />
    </div>
  );
}

function CampaignThumb({ campaign }: { campaign: Campaign }) {
  const image = campaign.product_image || campaign.creative_urls.find((url) => !/\.(mp4|mov)(\?|$)/i.test(url));
  return (
    <span className="flex h-11 w-11 flex-none items-center justify-center overflow-hidden rounded-xl bg-default-100">
      {image ? <img src={image} alt="" className="h-full w-full object-cover" /> : <Megaphone className="h-4 w-4 text-default-500" />}
    </span>
  );
}

function runs(campaign: Campaign) {
  if (campaign.starts_at) return `${shortDate(campaign.starts_at)} – ${shortDate(campaign.ends_at) ?? "ongoing"}`;
  return `${campaign.duration_days} days · created ${shortDate(campaign.created_at)}`;
}

function CampaignList({ campaigns }: { campaigns: Campaign[] }) {
  return (
    <>
      {/* Wide screens: a table. */}
      <div className="hidden overflow-x-auto md:block">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-default-200 text-left text-xs text-default-500">
              <th className="pb-2 font-medium">Campaign</th>
              <th className="pb-2 font-medium">Status</th>
              <th className="pb-2 text-right font-medium">Spend / budget</th>
              <th className="pb-2 text-right font-medium">Views</th>
              <th className="pb-2 text-right font-medium">Clicks</th>
              <th className="pb-2 text-right font-medium">Purchases</th>
              <th className="pb-2 text-right font-medium">ROAS</th>
              <th className="pb-2" />
            </tr>
          </thead>
          <tbody className="divide-y divide-default-100">
            {campaigns.map((campaign) => (
              <tr key={campaign.id}>
                <td className="py-3 pe-3">
                  <div className="flex items-center gap-3">
                    <CampaignThumb campaign={campaign} />
                    <div className="min-w-0">
                      <div className="truncate font-medium text-default-900">{campaign.title}</div>
                      <div className="text-xs text-default-500">{runs(campaign)}</div>
                    </div>
                  </div>
                </td>
                <td className="py-3"><CampaignStatus campaign={campaign} /></td>
                <td className="py-3 text-right tabular-nums">
                  {naira(campaign.spend)} <span className="text-default-500">/ {naira(campaign.budget_ngn)}</span>
                </td>
                <td className="py-3 text-right tabular-nums">{count(campaign.impressions)}</td>
                <td className="py-3 text-right tabular-nums">{count(campaign.clicks)}</td>
                <td className="py-3 text-right tabular-nums">{count(campaign.purchases)}</td>
                <td className="py-3 text-right tabular-nums">
                  {campaign.metrics.roas == null ? "-" : `${campaign.metrics.roas.toFixed(2)}x`}
                </td>
                <td className="py-3 ps-3">
                  <div className="flex items-center justify-end gap-3 whitespace-nowrap">
                    {campaign.live_ad_url && (
                      <a href={campaign.live_ad_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs font-medium text-default-700 hover:text-default-900">
                        View live ad <ExternalLink className="h-3 w-3" />
                      </a>
                    )}
                    <Button asChild size="sm" variant="outline">
                      <Link to={`/meta-ads/${campaign.id}`}>Details</Link>
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Phones: cards. */}
      <ul className="space-y-3 md:hidden">
        {campaigns.map((campaign) => (
          <li key={campaign.id} className="rounded-[14px] border border-default-200 p-4">
            <div className="flex items-start gap-3">
              <CampaignThumb campaign={campaign} />
              <div className="min-w-0 flex-1">
                <div className="truncate font-medium text-default-900">{campaign.title}</div>
                <div className="text-xs text-default-500">{runs(campaign)}</div>
              </div>
              <CampaignStatus campaign={campaign} />
            </div>
            <dl className="mt-3 grid grid-cols-3 gap-2 text-center">
              {[
                ["Spend", naira(campaign.spend)],
                ["Clicks", count(campaign.clicks)],
                ["Purchases", count(campaign.purchases)],
              ].map(([label, value]) => (
                <div key={label} className="rounded-xl bg-default-50 p-2">
                  <dt className="text-[11px] text-default-500">{label}</dt>
                  <dd className="text-sm font-semibold tabular-nums text-default-900">{value}</dd>
                </div>
              ))}
            </dl>
            <div className="mt-3 flex items-center justify-between gap-3">
              {campaign.live_ad_url ? (
                <a href={campaign.live_ad_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs font-medium text-default-700">
                  View live ad <ExternalLink className="h-3 w-3" />
                </a>
              ) : <span />}
              <Button asChild size="sm" variant="outline">
                <Link to={`/meta-ads/${campaign.id}`}>Details</Link>
              </Button>
            </div>
          </li>
        ))}
      </ul>
    </>
  );
}
